import type { CSSProperties } from 'react'
import { EditorTheme } from './templateEditor.theme'
import { Rect, ResizeHandle } from './templateEditor.types'

/** Ограничивает зум между minZoom и maxZoom. */
export const clampZoom = (zoom: number, theme: Pick<EditorTheme, 'minZoom' | 'maxZoom'>) =>
    Math.min(theme.maxZoom, Math.max(theme.minZoom, zoom))

/** Держит прямоугольник внутри холста и не меньше minElSize. */
export const clampRectToCanvas = (rect: Rect, canvasW: number, canvasH: number, minElSize: number): Rect => {
    const w = Math.min(Math.max(rect.w, minElSize), canvasW)
    const h = Math.min(Math.max(rect.h, minElSize), canvasH)
    const x = Math.min(Math.max(rect.x, 0), canvasW - w)
    const y = Math.min(Math.max(rect.y, 0), canvasH - h)

    return { x, y, w, h }
}

type ResizeEdges = {
    left: boolean
    right: boolean
    top: boolean
    bottom: boolean
}

const edgesFromHandle = (handle: ResizeHandle): ResizeEdges => ({
    left: handle.includes('w'),
    right: handle.includes('e'),
    top: handle.includes('n'),
    bottom: handle.includes('s'),
})

/** Тянем стороны/углы: меняем x/y/w/h от стартового rect. */
const applyHandleDelta = (start: Rect, edges: ResizeEdges, dx: number, dy: number): Rect => {
    let { x, y, w, h } = start

    if (edges.right) {
        w = start.w + dx
    }
    if (edges.bottom) {
        h = start.h + dy
    }
    if (edges.left) {
        x = start.x + dx
        w = start.w - dx
    }
    if (edges.top) {
        y = start.y + dy
        h = start.h - dy
    }

    return { x, y, w, h }
}

/** Подгоняет вторую сторону под ratio; якорь — противоположный край (или центр для боковых ручек). */
const applyAspectRatio = (
    start: Rect,
    rect: Rect,
    edges: ResizeEdges,
    ratio: number,
    dominantAxis: 'x' | 'y'
): Rect => {
    let { x, y, w, h } = rect

    if (dominantAxis === 'x') {
        h = w / ratio
        if (edges.top && !edges.bottom) {
            y = start.y + start.h - h
        } else if (!edges.top && !edges.bottom) {
            y = start.y + (start.h - h) / 2
        }
    } else {
        w = h * ratio
        if (edges.left && !edges.right) {
            x = start.x + start.w - w
        } else if (!edges.left && !edges.right) {
            x = start.x + (start.w - w) / 2
        }
    }

    return { x, y, w, h }
}

/** Не даём стать меньше minElSize; при ratio подтягиваем вторую сторону. */
const enforceMinSize = (
    start: Rect,
    rect: Rect,
    edges: ResizeEdges,
    minElSize: number,
    preserveRatio: boolean,
    ratio: number
): Rect => {
    let { x, y, w, h } = rect

    if (w < minElSize) {
        if (edges.left) {
            x = start.x + start.w - minElSize
        }
        w = minElSize
        if (preserveRatio) {
            h = w / ratio
            if (edges.top && !edges.bottom) {
                y = start.y + start.h - h
            } else if (!edges.top && !edges.bottom) {
                y = start.y + (start.h - h) / 2
            }
        }
    }

    if (h < minElSize) {
        if (edges.top) {
            y = start.y + start.h - minElSize
        }
        h = minElSize
        if (preserveRatio) {
            w = h * ratio
            if (edges.left && !edges.right) {
                x = start.x + start.w - w
            } else if (!edges.left && !edges.right) {
                x = start.x + (start.w - w) / 2
            }
        }
    }

    return { x, y, w, h }
}

/**
 * Новый rect после тяги за ручку ресайза.
 * 1) сдвигаем край(я) по dx/dy
 * 2) сохраняем ratio для угловых ручек (или для всех, если everyHandle)
 * 3) clamp по минимуму и границам холста
 */
export const computeResizeRect = (
    start: Rect,
    handle: ResizeHandle,
    dx: number,
    dy: number,
    canvasW: number,
    canvasH: number,
    minElSize: number,
    options?: { preserveRatio?: boolean; ratio?: number; everyHandle?: boolean }
): Rect => {
    const edges = edgesFromHandle(handle)
    const isCorner = handle.length === 2
    const ratio = options?.ratio && options.ratio > 0 ? options.ratio : start.w / Math.max(start.h, 1)
    const preserveRatio = Boolean(options?.preserveRatio) && (isCorner || Boolean(options?.everyHandle))

    let next = applyHandleDelta(start, edges, dx, dy)

    if (preserveRatio) {
        const dominantAxis =
            !edges.top && !edges.bottom
                ? 'x'
                : !edges.left && !edges.right
                ? 'y'
                : Math.abs(dx) >= Math.abs(dy)
                ? 'x'
                : 'y'
        next = applyAspectRatio(start, next, edges, ratio, dominantAxis)
    }

    next = enforceMinSize(start, next, edges, minElSize, preserveRatio, ratio)

    return clampRectToCanvas(next, canvasW, canvasH, minElSize)
}

/** Зум, при котором холст влезает в окно с отступами. */
export const calcFitZoom = (
    viewportW: number,
    viewportH: number,
    canvasW: number,
    canvasH: number,
    theme: Pick<EditorTheme, 'minZoom' | 'maxZoom' | 'fitPadding'>
) => {
    if (viewportW <= 0 || viewportH <= 0 || canvasW <= 0 || canvasH <= 0) {
        return 1
    }

    const availableW = Math.max(viewportW - theme.fitPadding * 2, 1)
    const availableH = Math.max(viewportH - theme.fitPadding * 2, 1)

    return clampZoom(Math.min(availableW / canvasW, availableH / canvasH), theme)
}

/** Форматирует 1.5 как «150%». */
export const formatZoomPercent = (zoom: number) => `${Math.round(zoom * 100)}%`

/** Логические пиксели холста → CSS-пиксели на экране. */
export const toDisplaySize = (logical: number, displayScale: number) => logical * displayScale

/** Область клика: не меньше minHitSize, центрирована внутри элемента. */
export const elementHitAreaStyle = (
    elementW: number,
    elementH: number,
    displayScale: number,
    minHitSize: number
): CSSProperties => {
    const displayW = toDisplaySize(elementW, displayScale)
    const displayH = toDisplaySize(elementH, displayScale)
    const minDisplay = toDisplaySize(minHitSize, displayScale)
    const hitW = Math.max(displayW, minDisplay)
    const hitH = Math.max(displayH, minDisplay)

    return {
        position: 'absolute',
        left: (displayW - hitW) / 2,
        top: (displayH - hitH) / 2,
        width: hitW,
        height: hitH,
        zIndex: 1,
    }
}

/** Ограничивает размер холста допустимым диапазоном. */
export const clampCanvasSize = (value: number, theme: Pick<EditorTheme, 'minCanvasSize' | 'maxCanvasSize'>) =>
    Math.round(Math.max(theme.minCanvasSize, Math.min(theme.maxCanvasSize, value)))
